import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { CSS_RESTRICTIONS } from '@common/constants';
import { EButtonHoverType } from '@common/enums/button.enum';

import type { ThemeColor, ThemeButtonData, ThemeButtonHover } from '@sites/site-theme/src/types';
import { ThemeCssUnit } from '@sites/site-theme/src/enums/css.enum';
import { BUTTON_HOVER_TYPE_OPTIONS } from '@sites/site-theme/src/config/vocabularies.const';

import { SpColorPickerV2Component, type ColorPickerEventPayload } from '@components/sp-color-picker/src/v2';
import { SpInputNumComponent } from '../../sp-form-controls/sp-input-num/sp-input-num.component';

import type { ThemeButtonEditorValue } from './sp-custom-button-v2.type';

/** Which colour fields follow the rest state while the hover only fades or casts a shadow. */
const HOVER_FOLLOWS_BASE = [EButtonHoverType.opacity, EButtonHoverType.shadow];

@Component({
    selector: 'sp-custom-button-v2',
    templateUrl: './sp-custom-button-v2.component.html',
    styleUrls: ['./sp-custom-button-v2.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, SpColorPickerV2Component, SpInputNumComponent],
})
export class SpCustomButtonV2Component {
    public readonly base = input.required<ThemeButtonData>();
    public readonly hover = input.required<ThemeButtonHover>();
    public readonly colorsPreset = input<ThemeColor[]>([]);

    public readonly valueChange = output<ThemeButtonEditorValue>();

    public readonly EButtonHoverType = EButtonHoverType;
    public readonly hoverOptions = BUTTON_HOVER_TYPE_OPTIONS;

    public readonly BORDER_WIDTH_MIN = CSS_RESTRICTIONS.BORDER_WIDTH_MIN;
    public readonly BORDER_WIDTH_MAX = CSS_RESTRICTIONS.BORDER_WIDTH_MAX;
    public readonly BORDER_RADIUS_MIN = CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    public readonly BORDER_RADIUS_MAX = CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    public readonly OPACITY_MIN = CSS_RESTRICTIONS.OPACITY_MIN;
    public readonly OPACITY_MAX = CSS_RESTRICTIONS.OPACITY_MAX;

    /**
     * The button being edited.
     *
     * Seeded from the inputs and written locally, so the pickers reflect a change before the host
     * has echoed it back. v1 achieved the same with a `FormGroup` it patched with
     * `{ emitEvent: false }`; here the seeding effect simply reads the inputs.
     */
    private readonly draft = signal<ThemeButtonEditorValue | null>(null);

    public readonly baseValue = computed(() => this.draft()?.base ?? this.base());
    public readonly hoverValue = computed(() => this.draft()?.hover ?? this.hover());

    /** Border lengths as the numeric inputs bind them; the unit is re-attached on write. */
    public readonly borderWidth = computed(() => this.baseValue().borderWidth.value);
    public readonly borderRadius = computed(() => this.baseValue().borderRadius.value);

    /**
     * A theme colour is stored as an array — one entry per button mode (light, dark) — and the
     * panel edits the light one. A single-entry array means "the same in both".
     */
    public readonly colorValue = computed(() => firstOf(this.baseValue().color));
    public readonly backgroundValue = computed(() => firstOf(this.baseValue().background));
    public readonly borderColorValue = computed(() => firstOf(this.baseValue().borderColor));
    public readonly hoverColorValue = computed(() => firstOf(this.hoverValue().color));
    public readonly hoverBackgroundValue = computed(() => firstOf(this.hoverValue().background));
    public readonly hoverBorderColorValue = computed(() => firstOf(this.hoverValue().borderColor));

    constructor() {
        // Inputs → draft. Re-runs when the host hands down a different button, which is what makes
        // switching between the primary and secondary tabs reset the editor.
        effect(() => {
            this.draft.set({ base: this.base(), hover: this.hover() });
        });
    }

    public setColor(payload: ColorPickerEventPayload): void {
        this.writeColor('color', payload);
    }

    public setBackground(payload: ColorPickerEventPayload): void {
        this.writeColor('background', payload);
    }

    public setBorderColor(payload: ColorPickerEventPayload): void {
        this.writeColor('borderColor', payload);
    }

    public setHoverColor(payload: ColorPickerEventPayload): void {
        this.writeHoverColor('color', payload);
    }

    public setHoverBackground(payload: ColorPickerEventPayload): void {
        this.writeHoverColor('background', payload);
    }

    public setHoverBorderColor(payload: ColorPickerEventPayload): void {
        this.writeHoverColor('borderColor', payload);
    }

    public setBorderWidth(value: number): void {
        this.write({ base: { ...this.baseValue(), borderWidth: { value, unit: ThemeCssUnit.px } } });
    }

    public setBorderRadius(value: number): void {
        this.write({ base: { ...this.baseValue(), borderRadius: { value, unit: ThemeCssUnit.px } } });
    }

    public setOpacity(opacity: number): void {
        this.write({ hover: { ...this.hoverValue(), opacity } });
    }

    /**
     * Switching the hover kind rewrites the whole hover state.
     *
     * Each kind has its own defaults, and the two that do not change colour take the rest state's,
     * so the button keeps its look and only the effect differs.
     */
    public changeHoverType(event: Event): void {
        const hoverType = (event.target as HTMLSelectElement).value as EButtonHoverType;
        const base = this.baseValue();

        if (hoverType === EButtonHoverType.color) {
            this.write({
                hover: {
                    hoverType,
                    opacity: 100,
                    shadow: 'none',
                    color: ['rgb(255,255,255)'],
                    background: ['rgb(22,117,242)'],
                    borderColor: ['rgba(0,0,0,0)'],
                },
            });

            return;
        }

        this.write({
            hover: {
                hoverType,
                opacity: hoverType === EButtonHoverType.opacity ? 80 : 100,
                shadow: hoverType === EButtonHoverType.shadow ? '0 0 10px 6px rgb(0 0 0 / 10%)' : 'none',
                color: base.color,
                background: base.background,
                borderColor: base.borderColor,
            },
        });
    }

    /**
     * A rest-state colour, and the hover's matching one when the hover is not meant to differ.
     *
     * Keeping them in step is what makes an opacity hover actually read as a fade — see the same
     * rule in the entity migration, which repairs themes stored before it was enforced.
     */
    private writeColor(property: 'color' | 'background' | 'borderColor', payload: ColorPickerEventPayload): void {
        const value = [payload.presetName ?? payload.value];
        const hover = this.hoverValue();
        const followsBase = HOVER_FOLLOWS_BASE.includes(hover.hoverType);

        this.write({
            base: { ...this.baseValue(), [property]: value },
            ...(followsBase ? { hover: { ...hover, [property]: value } } : {}),
        });
    }

    private writeHoverColor(property: 'color' | 'background' | 'borderColor', payload: ColorPickerEventPayload): void {
        this.write({ hover: { ...this.hoverValue(), [property]: [payload.presetName ?? payload.value] } });
    }

    private write(patch: Partial<ThemeButtonEditorValue>): void {
        const next: ThemeButtonEditorValue = {
            base: patch.base ?? this.baseValue(),
            hover: patch.hover ?? this.hoverValue(),
        };

        this.draft.set(next);
        this.valueChange.emit(next);
    }
}

/** The light-mode entry of a stored colour, or the colour itself if only one is stored. */
function firstOf(color: string | string[]): string {
    return Array.isArray(color) ? (color[0] ?? '') : color;
}
